import React, { useState, useEffect, useMemo } from 'react'
import { Button, Modal, message, Empty, Icon, Spin } from 'antd'
import './LocalReferencesView.scss'
import {
  findUsagesForKey,
  validateLocationKey,
  getBaseLocations,
  calculateNextKey
} from './utils/LocationUtils'
import LocationItem from './components/LocationItem'
import AddLocationModal from './components/AddLocationModal'
import BatchConfirmModal from './components/BatchConfirmModal'
import DeleteLocationModal from './components/DeleteLocationModal'

const LocalReferencesView = ({
  locationMap = {},
  locationReference = {},
  scenes = [],
  setFormData,
  onRegenerateLocationReference,
  onEditLocationReference,
  isGeneratingLocationReference,
  onBatchUpdateLocations,
  locationGenerationProgress,
  locationGenerationStatus
}) => {
  const [pendingEdits, setPendingEdits] = useState({})
  const displayMap = useMemo(
    () => ({
      ...(locationMap || {}),
      ...pendingEdits
    }),
    [locationMap, pendingEdits]
  )

  const keys = Object.keys(displayMap)

  const [modalVisible, setModalVisible] = useState(false)
  const [modalKey, setModalKey] = useState(null)
  const [modalUsages, setModalUsages] = useState([])
  const [replacementKey, setReplacementKey] = useState(null)
  const [regenerateModalVisible, setRegenerateModalVisible] = useState(false)
  const [editingKey, setEditingKey] = useState(null)
  const [editValue, setEditValue] = useState('')

  const [addModalVisible, setAddModalVisible] = useState(false)
  const [newLocationKey, setNewLocationKey] = useState('')
  const [newLocationDescription, setNewLocationDescription] = useState('')

  const [batchConfirmVisible, setBatchConfirmVisible] = useState(false)

  const [confirmSaveModalVisible, setConfirmSaveModalVisible] = useState(false)
  const [pendingSaveKey, setPendingSaveKey] = useState(null)

  const [addLocationType, setAddLocationType] = useState('new') // 'new' | 'existing'
  const [selectedBaseLocation, setSelectedBaseLocation] = useState(null)

  const [generatingKeys, setGeneratingKeys] = useState(null)
  const [loadingTip, setLoadingTip] = useState('')

  const openDeleteModal = key => {
    const usages = findUsagesForKey(key, scenes)
    setModalKey(key)
    setModalUsages(usages)
    setReplacementKey(null)
    setModalVisible(true)
  }

  const startEditing = (key, currentDescription) => {
    setEditingKey(key)
    setEditValue(currentDescription || '')
  }

  const cancelEditing = () => {
    setEditingKey(null)
    setEditValue('')
  }

  const saveEditing = key => {
    setPendingSaveKey(key)
    setConfirmSaveModalVisible(true)
  }

  const handleConfirmSave = () => {
    if (!pendingSaveKey) return

    setPendingEdits(prev => ({
      ...prev,
      [pendingSaveKey]: editValue
    }))
    message.success('Location description updated (unsaved)')

    setEditingKey(null)
    setEditValue('')
    setConfirmSaveModalVisible(false)
    setPendingSaveKey(null)
  }

  useEffect(() => {
    if (!isGeneratingLocationReference) {
      setGeneratingKeys(null)
      setLoadingTip('')
    }
  }, [isGeneratingLocationReference])

  const handleDelete = () => {
    const key = modalKey
    const usages = modalUsages || []

    const otherKeys = Object.keys(displayMap || {}).filter(k => k !== key)

    if (usages.length > 0 && otherKeys.length > 0 && !replacementKey) {
      message.error(
        'Please select an existing location to map prompts to before deleting'
      )
      return
    }

    try {
      let updatedScenes = scenes
      if (replacementKey) {
        const oldPlaceholder = `__${key}__`
        const newPlaceholder = `__${replacementKey}__`

        const replaceInText = text => {
          if (!text || typeof text !== 'string') return text
          return text
            .split(oldPlaceholder)
            .join(newPlaceholder)
            .split(key)
            .join(replacementKey)
        }

        updatedScenes = scenes.map(scene => {
          const newScene = { ...scene }
          if (newScene.description)
            newScene.description = replaceInText(newScene.description)
          if (Array.isArray(newScene.shots)) {
            newScene.shots = newScene.shots.map(shot => {
              const s = { ...shot }
              if (s.title) s.title = replaceInText(s.title)
              if (s.initial_image_prompt)
                s.initial_image_prompt = replaceInText(s.initial_image_prompt)
              if (s.video_prompt) s.video_prompt = replaceInText(s.video_prompt)
              if (s.dialogue) {
                const newDialogue = {}
                Object.entries(s.dialogue).forEach(([char, val]) => {
                  const text = typeof val === 'object' ? val.text : val
                  const newText = replaceInText(text)
                  if (typeof val === 'object') {
                    newDialogue[char] = { ...val, text: newText }
                  } else {
                    newDialogue[char] = newText
                  }
                })
                s.dialogue = newDialogue
              }
              return s
            })
          }
          return newScene
        })
      }

      // Remove from locationReference
      const newRefs = { ...(locationReference || {}) }
      const placeholderKey = `__${key}__`
      if (newRefs.hasOwnProperty(placeholderKey)) delete newRefs[placeholderKey]
      if (newRefs.hasOwnProperty(key)) delete newRefs[key]

      // If key was in pendingEdits, remove it
      if (pendingEdits[key]) {
        const newPending = { ...pendingEdits }
        delete newPending[key]
        setPendingEdits(newPending)
      }

      if (locationMap && locationMap.hasOwnProperty(key)) {
        const newMap = { ...(locationMap || {}) }
        if (newMap.hasOwnProperty(key)) delete newMap[key]
        if (newMap.hasOwnProperty(placeholderKey)) delete newMap[placeholderKey]
        setFormData(prev => ({
          ...prev,
          shotList: updatedScenes,
          locationReference: newRefs,
          locationMap: newMap
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          shotList: updatedScenes,
          locationReference: newRefs
        }))
      }

      setModalVisible(false)
      message.success('Location reference deleted')
    } catch (err) {
      console.error('Failed to delete location reference', err)
      message.error('Failed to delete location reference')
    }
  }

  const handleAddLocation = () => {
    setAddLocationType('new')
    setSelectedBaseLocation(null)
    const nextKey = calculateNextKey('new', null, Object.keys(displayMap || {}))
    setNewLocationKey(nextKey)
    setNewLocationDescription('')
    setAddModalVisible(true)
  }

  const submitAddLocation = () => {
    if (!newLocationKey || !newLocationDescription) {
      if (addLocationType === 'existing' && !selectedBaseLocation) {
        message.error('Please select a base location')
        return
      }
      message.error('Please provide description')
      return
    }

    if (!validateLocationKey(newLocationKey)) {
      message.error(
        'Location key must follow the format: __locationX.Y__ (e.g., __location1.1__)'
      )
      return
    }

    if (displayMap && displayMap.hasOwnProperty(newLocationKey)) {
      message.error('Location key already exists')
      return
    }

    setPendingEdits(prev => ({
      ...prev,
      [newLocationKey]: newLocationDescription
    }))
    setAddModalVisible(false)
    message.success('Location added (unsaved)')
  }

  const handleSaveBatch = () => {
    if (!onBatchUpdateLocations) return
    const modifiedKeys = Object.keys(pendingEdits)
    if (modifiedKeys.length === 0) return

    setBatchConfirmVisible(true)
  }

  const confirmBatchGeneration = () => {
    const modifiedKeys = Object.keys(pendingEdits)

    setFormData(prev => ({
      ...prev,
      locationMap: {
        ...(prev.locationMap || {}),
        ...pendingEdits
      }
    }))

    setPendingEdits({})
    setBatchConfirmVisible(false)
    onBatchUpdateLocations(displayMap, modifiedKeys)
    setGeneratingKeys(modifiedKeys)
    setLoadingTip('Regenerating locations...')
  }

  useEffect(() => {
    const currentKeys = Object.keys(displayMap || {})
    if (addModalVisible) {
      if (addLocationType === 'new') {
        setNewLocationKey(calculateNextKey('new', null, currentKeys))
      } else if (addLocationType === 'existing' && selectedBaseLocation) {
        setNewLocationKey(
          calculateNextKey('existing', selectedBaseLocation, currentKeys)
        )
      }
    }
  }, [addLocationType, selectedBaseLocation, addModalVisible, displayMap])

  return (
    <div className="local-references-container">
      <header className="lr-header">
        <div className="lr-info">
          <h2 className="lr-title">
            Locations List ({Object.keys(displayMap || {}).length} items)
          </h2>
          <p className="lr-description">
            Location references are used to generate locations for generating
            location-specific images for consistent locations in your video.
          </p>
        </div>
        <div className="lr-toolbar">
          {Object.keys(pendingEdits).length > 0 && (
            <Button
              type="primary"
              onClick={handleSaveBatch}
              disabled={isGeneratingLocationReference}
              icon={<Icon type="save" />}
            >
              Save & Regenerate ({Object.keys(pendingEdits).length})
            </Button>
          )}

          <div className="lr-list-actions">
            <Button
              disabled={isGeneratingLocationReference}
              onClick={handleAddLocation}
              icon="plus"
            >
              Add Location
            </Button>
            <Button
              disabled={isGeneratingLocationReference}
              onClick={() => {
                setRegenerateModalVisible(true)
              }}
              icon="reload"
            >
              Regenerate All References
            </Button>
          </div>
        </div>
      </header>

      <div className="lr-body">
        {!keys.length ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <Empty
              description={
                <span style={{ color: '#8c8c8c' }}>
                  No location references defined yet
                </span>
              }
            />
            <Button
              type="primary"
              onClick={handleAddLocation}
              style={{ marginTop: 24 }}
              icon="plus"
            >
              Add First Location
            </Button>
          </div>
        ) : (
          <div className="lr-content-wrapper">
            <div className="lr-items-grid">
              {keys.map(key => (
                <LocationItem
                  key={key}
                  locationKey={key}
                  description={displayMap[key]}
                  isPending={pendingEdits.hasOwnProperty(key)}
                  locationReference={locationReference}
                  isGeneratingLocationReference={isGeneratingLocationReference}
                  generatingKeys={generatingKeys}
                  loadingTip={loadingTip}
                  locationGenerationProgress={locationGenerationProgress}
                  locationGenerationStatus={locationGenerationStatus}
                  onEdit={startEditing}
                  onDelete={openDeleteModal}
                  onSave={saveEditing}
                  onCancelEdit={cancelEditing}
                  editingKey={editingKey}
                  editValue={editValue}
                  setEditValue={setEditValue}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <DeleteLocationModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onDelete={handleDelete}
        modalKey={modalKey}
        modalUsages={modalUsages}
        displayMap={displayMap}
        locationReference={locationReference}
        replacementKey={replacementKey}
        setReplacementKey={setReplacementKey}
      />

      {/* Regenerate confirmation modal */}
      <Modal
        visible={regenerateModalVisible}
        title="Regenerate location references"
        onCancel={() => setRegenerateModalVisible(false)}
        onOk={() => {
          setRegenerateModalVisible(false)
          if (onRegenerateLocationReference) {
            try {
              onRegenerateLocationReference()
              setGeneratingKeys('ALL')
              setLoadingTip('Regenerating all location references...')
              message.info('Regenerating location references...')
            } catch (err) {
              console.error('Failed to trigger regenerate:', err)
              message.error('Failed to start regeneration')
            }
          }
        }}
        okText="Yes, regenerate"
        cancelText="Cancel"
        centered
      >
        <p>
          This will overwrite existing location references. Are you sure you
          want to continue?
        </p>
      </Modal>

      <AddLocationModal
        visible={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={submitAddLocation}
        addLocationType={addLocationType}
        setAddLocationType={setAddLocationType}
        selectedBaseLocation={selectedBaseLocation}
        setSelectedBaseLocation={setSelectedBaseLocation}
        baseLocations={getBaseLocations(Object.keys(displayMap || {}))}
        newLocationKey={newLocationKey}
        newLocationDescription={newLocationDescription}
        setNewLocationDescription={setNewLocationDescription}
      />

      {/* Confirm Save Modal */}
      <Modal
        visible={confirmSaveModalVisible}
        title="Update Draft Description"
        onCancel={() => {
          setConfirmSaveModalVisible(false)
          setPendingSaveKey(null)
        }}
        onOk={handleConfirmSave}
        okText="Update Draft"
        cancelText="Cancel"
        centered
      >
        <p>
          This will update the description in your draft. You must click 'Save &
          Regenerate' to apply these changes and generate new images.
        </p>
      </Modal>

      <BatchConfirmModal
        visible={batchConfirmVisible}
        onCancel={() => setBatchConfirmVisible(false)}
        onOk={confirmBatchGeneration}
        pendingEdits={pendingEdits}
      />
    </div>
  )
}

export default LocalReferencesView
